import React, {useEffect, useState} from 'react';
import {ItemThumbPlaceholder} from '../../ItemPlaceholder/ItemThumbPlaceholder';
import {executeAction, formatDate, formatFiletype, formatSize, isNullOrEmpty} from '../../../utils';
import {LabelDTO} from '../../../models/dto/LabelDTO';
import {config} from '../../../init/config';
import {t} from 'i18next';
import {Label} from '../../../models/Label';
import {getLabel, isFile, isFolder} from '../../../utils/StorageObjectUtils';
import {Button} from '@bosch/react-frok';
import {
    BottomDetailsWrapper,
    ContextContainer,
    DescriptionSection,
    DetailsBar,
    DetailsBarSectionCenter,
    DetailsBarSectionLeft,
    DetailsBarSectionRight,
    DownloadButtonWrapper,
    ItemListTileContainer,
    ItemTitleWrapper,
    LabelImages
} from './ItemListTile.style';
import {LayoutViewType} from '../../../models/LayoutViewType';
import {useGetThumbnailUrl} from '../../../hooks/UseGetThumbnailUrl';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {getApplicableTemplateMetadataPropertySettingsMap, isVisibleComponent} from '../../../utils/TemplateUtils';
import {
    STORAGE_OBJECT_OBJECT_IS_PUBLIC,
    STORAGE_OBJECT_OBJECT_LABELS,
    STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION,
    STORAGE_OBJECT_OBJECT_LABELS_NAME,
    STORAGE_OBJECT_OBJECT_METADATA,
    STORAGE_OBJECT_OBJECT_METADATA_CONTENT_TYPE,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED,
    STORAGE_OBJECT_OBJECT_METADATA_TAGS,
    STORAGE_OBJECT_OBJECT_METADATA_THUMBNAIL_URL,
    STORAGE_OBJECT_OBJECT_METADATA_USER_LABELS,
    STORAGE_OBJECT_TOTAL_SIZE
} from '../../../init/storage_object_constants';
import {ItemViewProps} from '../ItemViewProps';
import {
    ITEMS_VIEW_COMMAND_ACTION,
    ITEMS_VIEW_COMMAND_ACTION_DOWNLOAD,
    ITEMS_VIEW_DATE_CREATED,
    ITEMS_VIEW_DESCRIPTION,
    ITEMS_VIEW_FILE_TYPE,
    ITEMS_VIEW_MOBILE_DATE_CREATED,
    ITEMS_VIEW_MOBILE_DESCRIPTION,
    ITEMS_VIEW_MOBILE_FILE_TYPE,
    ITEMS_VIEW_MOBILE_NAME,
    ITEMS_VIEW_MOBILE_PUBLIC_ACCESS,
    ITEMS_VIEW_MOBILE_TAGS,
    ITEMS_VIEW_MOBILE_THUMBNAIL,
    ITEMS_VIEW_MOBILE_TOTAL_SIZE,
    ITEMS_VIEW_MOBILE_USER_LABELS,
    ITEMS_VIEW_NAME,
    ITEMS_VIEW_PUBLIC_ACCESS,
    ITEMS_VIEW_TAGS,
    ITEMS_VIEW_THUMBNAIL,
    ITEMS_VIEW_TOTAL_SIZE,
    ITEMS_VIEW_USER_LABELS
} from '../../../init/ui_component_constants';
import {CommandActionsBar} from '../CommandActionsBar/CommandActionsBar';
import {isFileDownloadable} from '../../../utils/DtoUtils';

/**
 * @name ItemListTile
 * @description
 * The tile representing each folder/file on the main view as list item
 */
export const ItemListTile: React.VFC<ItemViewProps> = (props) => {
    const [templatePropertySettings] = useState<Map<string, TemplatePropertySettings>>(getApplicableTemplateMetadataPropertySettingsMap(props.item.objectType, props.item.templateId));
    const isFileItem = isFile(props.item.objectType);
    const isFolderItem = isFolder(props.item.objectType);
    const [label, setLabel] = useState<Label>(getLabel(props.defaultLocale, props.item.labels));
    const getThumbnailUrl = useGetThumbnailUrl();
    const [thumbnail, setThumbnail] = useState('');
    const setExistingThumbnail = async (thumbnailUrl: string) => {
        if (isNullOrEmpty(thumbnailUrl)) {
            return;
        }

        await getThumbnailUrl(thumbnailUrl)
            .then((existingThumbnail) => {
                if (!isNullOrEmpty(existingThumbnail)) {
                    setThumbnail(existingThumbnail);
                }
            })
            .catch();
    };

    useEffect(() => {
        if ((props.item.metadata.thumbnailUrl) && (!isNullOrEmpty(props.item.metadata.thumbnailUrl))) {
            setExistingThumbnail(props.item.metadata.thumbnailUrl).catch();
        }
    }, [props.item.metadata.thumbnailUrl]);

    useEffect(() => {
        setLabel(getLabel(props.defaultLocale, props.item.labels));
    }, [props.defaultLocale, props.item.labels]);

    let existingLabelFiltered: (LabelDTO | undefined)[] = [];
    if (config.LABEL_ENABLED) {
        const existingLabel = props.labelData?.map(existingKey => {
            if (props.item.metadata.userLabels?.includes(existingKey.labelKey)) {
                return existingKey;
            }
        });
        existingLabelFiltered = existingLabel.filter((Boolean));
    }

    let isDownloadable = (isFileItem && isFileDownloadable(props.item.status)) as boolean;

    return (
        <ItemListTileContainer id={generateIdentifier()}
                               className='a-tile -secondary'
                               onClick={(e) => executeAction(e, props.onClick)}>
            {((isVisibleComponent(ITEMS_VIEW_THUMBNAIL, templatePropertySettings)
                        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_THUMBNAIL, templatePropertySettings))))
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                    && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_THUMBNAIL_URL, templatePropertySettings)
                        || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_CONTENT_TYPE, templatePropertySettings))) &&
                <div id={generateIdentifier()} className='item-thumbnail'>
                    {((!!props.item.metadata.thumbnailUrl) && (!isNullOrEmpty(props.item.metadata.thumbnailUrl))) ?
                        <img id={generateIdentifier()}
                             className='-primary'
                             src={thumbnail}
                             alt={'thumbnail'}/>
                        : <ItemThumbPlaceholder
                            contentType={isFolderItem ? 'folder' : props.item.metadata.contentType ?? null}
                            viewType={LayoutViewType.LIST}/>
                    }
                </div>
            }
            {(((isVisibleComponent(ITEMS_VIEW_TOTAL_SIZE, templatePropertySettings)
                            && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_TOTAL_SIZE, templatePropertySettings))))
                        && isVisibleComponent(STORAGE_OBJECT_TOTAL_SIZE, templatePropertySettings))
                    || ((props.editMode && (isVisibleComponent(ITEMS_VIEW_PUBLIC_ACCESS, templatePropertySettings)
                            && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_PUBLIC_ACCESS, templatePropertySettings)))))
                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_IS_PUBLIC, templatePropertySettings))
                    || (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                        && (((isVisibleComponent(ITEMS_VIEW_DATE_CREATED, templatePropertySettings)
                                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_DATE_CREATED, templatePropertySettings))))
                                && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED, templatePropertySettings))
                            || ((isVisibleComponent(ITEMS_VIEW_FILE_TYPE, templatePropertySettings)
                                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_FILE_TYPE, templatePropertySettings))))
                                && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_CONTENT_TYPE, templatePropertySettings))
                            || ((isVisibleComponent(ITEMS_VIEW_TAGS, templatePropertySettings)
                                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_TAGS, templatePropertySettings))))
                                && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_TAGS, templatePropertySettings))
                            || ((isVisibleComponent(ITEMS_VIEW_USER_LABELS, templatePropertySettings)
                                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_USER_LABELS, templatePropertySettings))))
                                && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_USER_LABELS, templatePropertySettings))))
                    || (isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, templatePropertySettings)
                        && (((isVisibleComponent(ITEMS_VIEW_NAME, templatePropertySettings)
                                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_NAME, templatePropertySettings))))
                                && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, templatePropertySettings))
                            || ((isVisibleComponent(ITEMS_VIEW_DESCRIPTION, templatePropertySettings)
                                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_DESCRIPTION, templatePropertySettings))))
                                && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION, templatePropertySettings))))) &&
                <ContextContainer id={generateIdentifier()} className='a-text details-container'>
                    {(((isVisibleComponent(ITEMS_VIEW_TOTAL_SIZE, templatePropertySettings)
                                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_TOTAL_SIZE, templatePropertySettings))))
                                && isVisibleComponent(STORAGE_OBJECT_TOTAL_SIZE, templatePropertySettings))
                            || (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                                && (((isVisibleComponent(ITEMS_VIEW_DATE_CREATED, templatePropertySettings)
                                            && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_DATE_CREATED, templatePropertySettings))))
                                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED, templatePropertySettings))
                                    || ((isVisibleComponent(ITEMS_VIEW_FILE_TYPE, templatePropertySettings)
                                            && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_FILE_TYPE, templatePropertySettings))))
                                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_CONTENT_TYPE, templatePropertySettings))
                                    || ((isVisibleComponent(ITEMS_VIEW_TAGS, templatePropertySettings)
                                            && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_TAGS, templatePropertySettings))))
                                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_TAGS, templatePropertySettings))
                                    || ((isVisibleComponent(ITEMS_VIEW_USER_LABELS, templatePropertySettings)
                                            && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_USER_LABELS, templatePropertySettings))))
                                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_USER_LABELS, templatePropertySettings))))) &&
                        <DetailsBar id={generateIdentifier('item-details-bar')} className='item-details-bar'>
                            {(((isVisibleComponent(ITEMS_VIEW_TOTAL_SIZE, templatePropertySettings)
                                            && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_TOTAL_SIZE, templatePropertySettings))))
                                        && isVisibleComponent(STORAGE_OBJECT_TOTAL_SIZE, templatePropertySettings))
                                    || (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                                        && (((isVisibleComponent(ITEMS_VIEW_DATE_CREATED, templatePropertySettings)
                                                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_DATE_CREATED, templatePropertySettings))))
                                                && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED, templatePropertySettings))
                                            || ((isVisibleComponent(ITEMS_VIEW_FILE_TYPE, templatePropertySettings)
                                                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_FILE_TYPE, templatePropertySettings))))
                                                && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_CONTENT_TYPE, templatePropertySettings))
                                            || ((isVisibleComponent(ITEMS_VIEW_TAGS, templatePropertySettings)
                                                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_TAGS, templatePropertySettings))))
                                                && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_TAGS, templatePropertySettings))))) &&
                                <DetailsBarSectionLeft id={generateIdentifier()}>
                                    <div id={generateIdentifier('id={generateIdentifier(')}
                                         className='id={generateIdentifier('>
                                        {((isVisibleComponent(ITEMS_VIEW_DATE_CREATED, templatePropertySettings)
                                                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_DATE_CREATED, templatePropertySettings))))
                                                && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                                                && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED, templatePropertySettings)) &&
                                            <span id={generateIdentifier()} className='-size-s'>
                                                {formatDate(props.language, props.item.metadata.dateCreated)}
                                            </span>
                                        }
                                        {((isVisibleComponent(ITEMS_VIEW_TOTAL_SIZE, templatePropertySettings)
                                                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_TOTAL_SIZE, templatePropertySettings))))
                                                && isVisibleComponent(STORAGE_OBJECT_TOTAL_SIZE, templatePropertySettings)) &&
                                            <span id={generateIdentifier()} className='-size-s'>
                                                {' '} | {formatSize(props.item.totalSize)}
                                            </span>
                                        }
                                        {((isVisibleComponent(ITEMS_VIEW_FILE_TYPE, templatePropertySettings)
                                                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_FILE_TYPE, templatePropertySettings))))
                                                && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                                                && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_CONTENT_TYPE, templatePropertySettings)) &&
                                            <span id={generateIdentifier()} className='-size-s'>
                                                {' '} | {formatFiletype(isFolderItem ? 'folder' : props.item.metadata.contentType ?? null)}
                                            </span>
                                        }
                                    </div>
                                    {((isVisibleComponent(ITEMS_VIEW_TAGS, templatePropertySettings)
                                                && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_TAGS, templatePropertySettings))))
                                            && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                                            && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_TAGS, templatePropertySettings)) &&
                                        <div id={generateIdentifier('item-details-rows')} className='item-details-rows'>
                                            <span id={generateIdentifier()} className='-size-s'>
                                                {(props.item.metadata.tags && (props.item.metadata.tags.length > 0)) && props.item.metadata.tags.join(', ')}
                                            </span>
                                        </div>
                                    }
                                </DetailsBarSectionLeft>
                            }
                            {((isVisibleComponent(ITEMS_VIEW_USER_LABELS, templatePropertySettings)
                                        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_USER_LABELS, templatePropertySettings))))
                                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_USER_LABELS, templatePropertySettings)) &&
                                <DetailsBarSectionCenter id={generateIdentifier()}>
                                    {existingLabelFiltered?.map(existingLabel => {
                                        return <div id={generateIdentifier('item-details-label-column')}
                                                    className='item-details-label-column'
                                                    key={existingLabel?.labelKey}>
                                            <LabelImages id={generateIdentifier()}
                                                         src={existingLabel?.label}
                                                         alt={'label'}/>
                                        </div>
                                    })}
                                </DetailsBarSectionCenter>
                            }
                            <DetailsBarSectionRight id={generateIdentifier()}>
                                {(isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION, templatePropertySettings)) &&
                                    <CommandActionsBar templatePropertySettings={templatePropertySettings}
                                                       isMobileView={props.isMobileView}
                                                       layoutView={props.layoutView}
                                                       item={props.item}
                                                       editMode={props.editMode}
                                                       publicOnlyAccess={props.publicOnlyAccess}
                                                       isFavorite={props.isFavorite}
                                                       onDetails={props.onDetails}
                                                       onEdit={props.onEdit}
                                                       onDelete={props.onDelete}
                                                       onDownload={props.onDownload}
                                                       onFavorite={props.onFavorite}
                                                       onPreview={props.onPreview}
                                                       onCopyUrl={props.onCopyUrl}/>
                                }
                            </DetailsBarSectionRight>
                        </DetailsBar>
                    }
                    {((isVisibleComponent(ITEMS_VIEW_NAME, templatePropertySettings)
                                && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_NAME, templatePropertySettings))))
                            && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, templatePropertySettings)
                            && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, templatePropertySettings)) &&
                        <ItemTitleWrapper id={generateIdentifier()}>
                            <h3 id={generateIdentifier()} className='-size-xl item-title'>
                                <div id={generateIdentifier()}>
                                    <div id={generateIdentifier()}>
                                        {label.name}
                                    </div>
                                    <i id={generateIdentifier()} className='a-icon ui-ic-inline-right item-chevron'
                                       title='inline-right'/>
                                </div>
                            </h3>
                        </ItemTitleWrapper>
                    }
                    <BottomDetailsWrapper id={generateIdentifier()}>
                        {(((props.editMode && (isVisibleComponent(ITEMS_VIEW_PUBLIC_ACCESS, templatePropertySettings)
                                        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_PUBLIC_ACCESS, templatePropertySettings)))))
                                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_IS_PUBLIC, templatePropertySettings))
                                || ((isVisibleComponent(ITEMS_VIEW_DESCRIPTION, templatePropertySettings)
                                        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_DESCRIPTION, templatePropertySettings))))
                                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, templatePropertySettings)
                                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION, templatePropertySettings))) &&
                            <DescriptionSection id={generateIdentifier('item-description')}
                                                className='item-description a-text-area'>
                                {((props.editMode && (isVisibleComponent(ITEMS_VIEW_PUBLIC_ACCESS, templatePropertySettings)
                                            && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_PUBLIC_ACCESS, templatePropertySettings)))))
                                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_IS_PUBLIC, templatePropertySettings)) &&
                                    <p id={generateIdentifier('item-description')} className='item-description -size-m'>
                                        {t(isFolderItem ? 'FOLDER_DETAILS_PUBLIC_ACCESS' : 'FILE_DETAILS_PUBLIC_ACCESS',
                                            {isPublic: props.item.isPublic ? t('PUBLIC_ACCESS_TRUE_VALUE') : t('PUBLIC_ACCESS_FALSE_VALUE')})
                                        }
                                    </p>
                                }
                                {((isVisibleComponent(ITEMS_VIEW_DESCRIPTION, templatePropertySettings)
                                            && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_VIEW_MOBILE_DESCRIPTION, templatePropertySettings))))
                                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, templatePropertySettings)
                                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION, templatePropertySettings)) &&
                                    label.description
                                }
                            </DescriptionSection>
                        }
                        {(isDownloadable && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_DOWNLOAD, templatePropertySettings)) &&
                            <DownloadButtonWrapper id={generateIdentifier()}>
                                <Button id={generateIdentifier()}
                                        className='a-button a-button--secondary -without-label'
                                        key='download'
                                        icon='download'
                                        mode='secondary'
                                        type='button'
                                        title={t('GENERAL_DOWNLOAD_LABEL')}
                                        onClick={(e) => executeAction(e, props.onDownload)}/>
                            </DownloadButtonWrapper>
                        }
                    </BottomDetailsWrapper>
                </ContextContainer>
            }
        </ItemListTileContainer>
    );
};
